import { IdReferencia } from "@/shared/perms.config";
import { GenericService } from "../generic-service";

/**
 * Tipos para o serviço de permissões
 */
export type IdPermissao = IdReferencia; // ex: "usuarios.listar", "inbox.ver_grupos"
export type IdUsuario = string;
export type IdConta = string;
export type IdInbox = string | null;

/**
 * DTO para atualizar permissões - mapeia AtualizarPermissoesDto do backend
 */
export type AtualizarPermissoesRequest = {
  idUsuario: IdUsuario;
  idConta: IdConta;
  idInbox?: IdInbox;
  permissoesIds: IdPermissao[];
};

/**
 * DTO para consultar permissões - mapeia ConsultaPermissoesDto do backend
 */
export type ConsultaPermissoesRequest = {
  idUsuario: IdUsuario;
  idConta: IdConta;
  idInbox?: IdInbox;
};

/**
 * DTO para deletar permissão individual - mapeia DeletarPermissaoDto do backend
 */
export type DeletarPermissaoRequest = {
  idUsuario: IdUsuario;
  idPermissao: IdPermissao;
  idConta: IdConta;
  idInbox?: IdInbox;
};

/**
 * DTO para verificar permissão - mapeia VerificarPermissaoDto do backend
 */
export type VerificarPermissaoRequest = {
  idUsuario: IdUsuario;
  idPermissao: IdPermissao;
  idConta?: IdConta;
  idInbox?: IdInbox;
};

/**
 * Resposta ao atualizar permissões
 */
export type AtualizarPermissoesResponse = {
  sucesso: boolean;
  mensagem: string;
};

/**
 * Resposta ao deletar permissão
 */
export type DeletarPermissaoResponse = {
  sucesso: boolean;
  mensagem: string;
};

/**
 * Resposta ao verificar permissão
 */
export type VerificarPermissaoResponse = {
  temPermissao: boolean;
};

/**
 * Permissão com status de ativo/inativo para um usuário
 */
export type PermissaoComStatus = {
  id: IdPermissao;
  titulo: string;
  descricao: string;
  ativa: boolean;
};

/**
 * Configuração de permissão disponível no sistema
 */
export type PermissaoConfig = {
  idReferencia: IdPermissao;
  titulo: string;
  descricao: string;
  escopo: "conta" | "inbox";
  valorPadrao: boolean;
  grupo?: string;
  isHeader?: boolean;
  icon?: string;
};

/**
 * Service para gerenciar permissões de usuários
 *
 * Implementa operações específicas para:
 * - Atualizar permissões (substitui todas de um contexto)
 * - Obter permissões (retorna IDs das permissões do usuário)
 * - Obter permissões com status (retorna todas as permissões com status ativo/inativo)
 * - Verificar se tem permissão específica
 * - Deletar permissão individual (desmarca um checkbox)
 * - Listar todas as permissões disponíveis no sistema
 */
export class UsuarioPermissoesService extends GenericService<any, any, any> {
  constructor() {
    super("usuario-permissoes");
  }

  /**
   * Atualiza todas as permissões de um usuário em um contexto (conta/inbox)
   * Remove todas as permissões antigas e cria as novas em uma transaction
   *
   * @param idUsuario ID do usuário
   * @param idConta ID da conta
   * @param idInbox ID do inbox (opcional, null para permissões de conta)
   * @param permissoesIds Array com IDs das permissões a atribuir
   * @returns Promise com resultado da operação
   */
  async atualizarPermissoes(
    idUsuario: IdUsuario,
    idConta: IdConta,
    idInbox: IdInbox,
    permissoesIds: IdPermissao[],
  ): Promise<AtualizarPermissoesResponse> {
    const payload: AtualizarPermissoesRequest = {
      idUsuario,
      idConta,
      ...(idInbox && { idInbox }),
      permissoesIds,
    };

    return (await this.f.post("/", payload)) as AtualizarPermissoesResponse;
  }

  /**
   * Obtém as permissões que um usuário possui em um contexto
   * Retorna apenas as permissões ativas do usuário com seus dados
   *
   * @param idUsuario ID do usuário
   * @param idConta ID da conta
   * @param idInbox ID do inbox (opcional)
   * @returns Promise com array de permissões (id, titulo, descricao)
   */
  async obterTodasPermissoes(
    idUsuario: IdUsuario,
  ): Promise<
    Record<string, { conta: string[]; inboxes: Record<string, string[]> }>
  > {
    const params = new URLSearchParams({ idUsuario });
    const resultado = await this.f.get(`/todas?${params.toString()}`);
    return resultado as Record<
      string,
      { conta: string[]; inboxes: Record<string, string[]> }
    >;
  }

  /**
   * Obtém as permissões do usuário a partir do cache (Redis)
   * Estrutura otimizada para performance
   */
  async obterPermissoesDoCache(
    idUsuario: IdUsuario,
  ): Promise<
    Record<string, { conta: IdReferencia[]; inboxes: Record<string, IdReferencia[]> }>
  > {
    const params = new URLSearchParams({ idUsuario });
    // Ajuste o endpoint conforme sua rota backend. O usuário mencionou @Get("/cache")
    // Se o controller for "usuario-permissoes", então é /usuario-permissoes/cache
    const resultado = await this.f.get(`/cache?${params.toString()}`);
    return resultado as Record<
      string,
      { conta: IdReferencia[]; inboxes: Record<string, IdReferencia[]> }
    >;
  }

  /**
   * Verifica se um usuário possui uma permissão específica em um contexto
   *
   * @param idUsuario ID do usuário
   * @param idPermissao ID da permissão a verificar
   * @param idConta ID da conta (opcional para algumas permissões)
   * @param idInbox ID do inbox (opcional para permissões de inbox)
   * @returns Promise com resultado booleano da verificação
   */
  async verificarPermissao(
    idUsuario: IdUsuario,
    idPermissao: IdPermissao,
    idConta?: IdConta,
    idInbox?: IdInbox,
  ): Promise<VerificarPermissaoResponse> {
    const params = new URLSearchParams({
      idUsuario,
      idPermissao,
      ...(idConta && { idConta }),
      ...(idInbox && { idInbox }),
    });

    return (await this.f.get(
      `/verificar?${params.toString()}`,
    )) as VerificarPermissaoResponse;
  }

  /**
   * Deleta uma permissão individual do usuário
   * Usado quando um checkbox é desmarcado no frontend
   *
   * @param idUsuario ID do usuário
   * @param idPermissao ID da permissão a remover
   * @param idConta ID da conta
   * @param idInbox ID do inbox (opcional)
   * @returns Promise com resultado da operação
   */
  async deletarPermissao(
    idUsuario: IdUsuario,
    idPermissao: IdPermissao,
    idConta: IdConta,
    idInbox?: IdInbox,
  ): Promise<DeletarPermissaoResponse> {
    const payload: DeletarPermissaoRequest = {
      idUsuario,
      idPermissao,
      idConta,
      ...(idInbox && { idInbox }),
    };

    return (await this.f.delete("/", payload)) as DeletarPermissaoResponse;
  }

  /**
   * Obtém todas as permissões disponíveis no sistema
   * Retorna as permissões definidas em permissoes.config.ts
   *
   * @param escopo Filtrar por escopo: "conta" ou "inbox" (opcional)
   * @returns Array com todas as permissões disponíveis (ou filtradas por escopo)
   */
  // obterTodasPermissoes(escopo?: "conta" | "inbox"): PermissaoConfig[] {
  //   if (!escopo) {
  //     return PERMISSOES_CONFIG;
  //   }

  //   return PERMISSOES_CONFIG.filter(
  //     (p) => p.escopo === escopo && p.valorPadrao === true,
  //   );
  // }

  /**
   * Helper para construir query params para requisições GET
   * Evita incluir valores undefined na URL
   *
   * @param params Objeto com parâmetros
   * @returns String formatada para usar na URL
   */
  private construirQueryParams(
    params: Record<string, string | undefined>,
  ): string {
    const filtered = Object.entries(params)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${encodeURIComponent(value!)}`)
      .join("&");

    return filtered ? `?${filtered}` : "";
  }
}
